import { type AuthProvider, type HttpError } from "@refinedev/core";
import { message } from "antd";
import { Amplify } from "aws-amplify";
import {
  confirmResetPassword,
  confirmSignIn,
  fetchUserAttributes,
  getCurrentUser,
  signOut,
  signUp,
  updatePassword,
} from "aws-amplify/auth";
import { filter, find } from "lodash-es";
import { getLoginRedirectUrl } from "utils/common";
import type { AuthResult } from "utils/types/user.type";
import { UserRoleType } from "utils/types/user.type";

import { useGlobalStore } from "./store";
import { axiosInstance } from "./utils";

export const TOKEN_KEY = "refine-auth";
const ROLE_KEY = "role";
// const url = import.meta.env.VITE_API_URL;
const TIMEOUT = import.meta.env.VITE_NETWORK_TIMEOUT;
class AccessTokenRekovedError extends Error {}
class UserRoleError extends Error {} // 用户角色错误

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_USER_POOL_APP_CLIENT_ID,
    },
  },
});
const MAX_OTP_RETRY = 2;

export const authProvider: AuthProvider = {
  login: async (authParams) => {
    const timeOutAttempt = new Promise<"timeout">((resolve) => {
      setTimeout(resolve, TIMEOUT, "timeout");
    });
    const loginAttempt = async () => {
      let user;
      try {
        const result = await confirmSignIn({
          challengeResponse: authParams.code,
        });

        // 验证码错误
        if (!result.isSignedIn) throw new Error("確認コードが正しくありません。");

        // 验证码成功 但是用户没有在aws中被找到; ps,这个场景几乎不可能;
        user = await getCurrentUser();
        if (!user) {
          throw new Error("No user profile found.");
        }

        // 添加role的检查, 比如锁箱施工者等角色不能进入desk/admin系统;
        const { data } = await axiosInstance.get<AuthResult>("/auth");
        const { user: useRoleData } = data;

        if (useRoleData?.roles && useRoleData?.roles.length) {
          const roles = useRoleData.roles;
          const findRole = find(roles, ({ role }: { role: UserRoleType }) =>
            [
              UserRoleType.SystemManager,
              UserRoleType.TenantManager,
              UserRoleType.PropertyManager,
              UserRoleType.PropertyStaff,
            ].includes(role)
          );
          if (!findRole) {
            throw new UserRoleError("利用権限がありません。");
          }
        } else {
          throw new UserRoleError("利用権限がありません。");
        }

        message.success("ログインに成功しました。");

        return {
          success: true,
        };
      } catch (error: any) {
        if (authParams.retryCount >= MAX_OTP_RETRY) {
          throw new Error("確認コードの入力回数が上限に達しました。確認コードを「再送信」してください。");
        }

        if (error.message.includes("page was refreshed during the sign in flow")) {
          throw new Error("確認コードが正しくありません。");
        }

        if (error.message === "Invalid session for the user.") {
          throw new Error("確認コードの有効期限が切れました。確認コードを「再送信」してください。");
        }

        if (error instanceof UserRoleError) {
          await authProvider.logout({});
          const loginUrl = getLoginRedirectUrl("/login");
          const separator = loginUrl.includes("?") ? "&" : "?";
          return {
            success: false,
            error,
            redirectTo: `${loginUrl}${separator}force=true`,
          };
        }
        return {
          success: false,
          error,
        };
      }
    };
    const result = await Promise.race([timeOutAttempt, loginAttempt()]);
    if (result === "timeout") {
      return {
        success: false,
        error: new Error("ネットワークがタイムアウトしました。"),
      };
    } else return result;
  },
  logout: async () => {
    try {
      await signOut();
      sessionStorage.removeItem(ROLE_KEY);

      return {
        success: true,
        redirectTo: "/login",
      };
    } catch (error) {
      return {
        success: false,
        error: error as Error,
      };
    }
  },
  check: async () => {
    try {
      const { data } = await axiosInstance.get<AuthResult>("/auth");
      const { user } = data;

      // 本来是有效的阻拦非法用户登录代码, 现在变成保险代码了, 见同个commit的内容;
      if (user?.roles && user?.roles.length) {
        const roles = user.roles;
        const findRole = find(roles, ({ role }: { role: UserRoleType }) =>
          [
            UserRoleType.SystemManager,
            UserRoleType.TenantManager,
            UserRoleType.PropertyManager,
            UserRoleType.PropertyStaff,
          ].includes(role)
        );
        if (!findRole) {
          await authProvider.logout({});
          return {
            logout: true,
            authenticated: false,
            redirectTo: getLoginRedirectUrl("/login"),
          };
        }
      }

      if (user?.roles) {
        if (user.roles.length) {
          useGlobalStore.getState().setRoles(user.roles);
          sessionStorage.setItem(ROLE_KEY, user.roles?.[0].role);
        }
      }

      if (!user) {
        throw new Error("No user profile found.");
      }
      await fetchUserAttributes().catch((e) => {
        if (e?.message && e?.message?.match(/Access Token has been revoked/)) {
          throw new AccessTokenRekovedError();
        }
      });
    } catch (error) {
      if (error instanceof AccessTokenRekovedError) {
        await signOut();
      }
      return {
        authenticated: false,
        redirectTo: getLoginRedirectUrl("/login"),
      };
    }
    return {
      authenticated: true,
    };
  },
  getPermissions: async (params) => {
    const { data } = await axiosInstance.get<AuthResult>("/auth");
    return filter(data.user?.roles, params);
  },
  // getPermissions: async () => {
  //   console.log("checked permissions");
  //   const { roles, propertyId } = useGlobalStore.getState();
  //   const { data: menuData } = await axiosInstance.get<Menu[]>(
  //     `/properties/${propertyId}/menu`
  //   );
  //   let allowedResources = new Set<string>();
  //   const getDeviceMenu = async () => {
  //     const { data: propertyDetail } = await axiosInstance.get<Property>(
  //       `/properties/${propertyId}`
  //     );
  //     const { data: deviceSummaryData } =
  //       await axiosInstance.get<DeviceSummary>(
  //         `/properties/${propertyId}/devices/overview`
  //       );
  //     if (
  //       deviceSummaryData.PabbitLite ||
  //       find(propertyDetail.device_contracts, {
  //         device_type: DeviceType.PabbitLite,
  //       })
  //     ) {
  //       allowedResources.add("PabbitLite");
  //     }
  //     if (
  //       deviceSummaryData.PATMO ||
  //       find(propertyDetail.device_contracts, { device_type: DeviceType.PATMO })
  //     ) {
  //       allowedResources.add("PATMO");
  //     }
  //     if (
  //       deviceSummaryData.dearisController ||
  //       find(propertyDetail.device_contracts, {
  //         device_type: DeviceType.dearisController,
  //       })
  //     ) {
  //       allowedResources.add("dearis");
  //     }
  //   };
  //   const { data: serviceData } = await axiosInstance.get<PropertyService[]>(
  //     `/properties/${propertyId}/services`
  //   );
  //   if (
  //     find(roles, ({ role }) =>
  //       ["PropertyManager", "PropertyStaff"].includes(role)
  //     )
  //   ) {
  //     allowedResources = new Set(
  //       menuData.filter(({ visibility }) => visibility).map(({ name }) => name)
  //     );
  //     await getDeviceMenu();
  //     serviceData.forEach(({ type, status }) => {
  //       if (type === "temporaryPass" && status === "active") {
  //         allowedResources.add("temp_access");
  //       }
  //     });
  //     if (find(roles, { role: "PropertyStaff", property_id: propertyId })) {
  //       allowedResources.delete("account_management");
  //     }
  //     return allowedResources;
  //   } else {
  //     allowedResources = new Set(menuData.map(({ name }) => name));
  //     await getDeviceMenu();
  //     serviceData.forEach(({ type, status }) => {
  //       if (type === "temporaryPass" && status === "active") {
  //         allowedResources.add("temp_access");
  //       }
  //     });
  //     return allowedResources;
  //   }
  // },
  getIdentity: async () => {
    const result = await axiosInstance.get<AuthResult>("/auth");
    const user = result.data.user;
    return user;
  },
  onError: async (error: HttpError) => {
    if (error.statusCode === 0 && error.message === "") {
      console.error(error);
      return {
        error,
      };
    }
    if (error.statusCode === 401) {
      return {
        logout: true,
        redirectTo: getLoginRedirectUrl("/login"),
        error,
      };
    } else if (error.statusCode === 403) {
      return {
        logout: true,
        redirectTo: getLoginRedirectUrl("/login", true),
        error,
      };
    } else return { error };
  },
  forgotPassword: async ({ email, code, password }) => {
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: password,
      });

      return {
        success: true,
        redirectTo: "/login",
      };
    } catch (error) {
      return {
        success: false,
        error: error as Error,
      };
    }
  },
  updatePassword: async ({ password, newPassword }) => {
    try {
      await updatePassword({ oldPassword: password, newPassword });
      await signOut();

      return {
        success: true,
        redirectTo: "/login",
      };
    } catch (error) {
      return {
        success: false,
      };
    }
  },
  register: async (data) => {
    // console.log(data);
    await signUp({
      username: data.email,
      password: data.password,
    });
    return {
      success: false,
    };
  },
};
